import { Accordion, Anchor, Text } from '@mantine/core'
import type {
  QuestionnaireItem,
  QuestionnaireResponseItem,
} from '@medplum/fhirtypes'
import { FormSection, QuestionnaireItemType } from '@medplum/react'
import { invariant } from '@seen/core/invariant'
import * as structureDefinitions from '@seen/core/structureDefinitions'
import { type JSX, useState } from 'react'
import { QuestionnaireFormItem } from '#src/components/questionnaire-form/QuestionnaireFormItem.tsx'

interface QuestionnaireRepeatableItemProps {
  readonly item: QuestionnaireItem
  readonly response?: QuestionnaireResponseItem
  readonly checkForQuestionEnabled: (item: QuestionnaireItem) => boolean
  readonly onChange: (items: QuestionnaireResponseItem[]) => void
}

export function QuestionnaireRepeatableItem(
  props: QuestionnaireRepeatableItemProps
): JSX.Element | null {
  const { item, response, onChange } = props
  const [number, setNumber] = useState(
    getNumberOfRepeats(item, response ?? { linkId: item.linkId })
  )
  if (!props.checkForQuestionEnabled(item)) {
    return null
  }

  if (!response) {
    return null
  }

  function isQuestionnaireItemLink(item: QuestionnaireItem): boolean {
    return !!item.extension?.some(
      (e) => e.url === structureDefinitions.QUESTIONNAIRE_ITEM_DISPLAY_LINK_URL
    )
  }

  function isQuestionnaireItemAccordion(item: QuestionnaireItem): boolean {
    return !!item.extension?.some(
      (e) =>
        e.url === structureDefinitions.QUESTIONNAIRE_ITEM_DISPLAY_ACCORDION_URL
    )
  }

  if (item.type === QuestionnaireItemType.display) {
    if (isQuestionnaireItemLink(props.item)) {
      return (
        <Anchor
          href={props.item.extension?.[0]?.valueString}
          target="_blank"
          rel="noreferrer"
        >
          {props.item.text}
        </Anchor>
      )
    } else if (isQuestionnaireItemAccordion(props.item)) {
      invariant(props.item.text, 'Accordion item must have text')
      return (
        <Accordion variant="filled" chevronPosition="left">
          <Accordion.Item key={props.item.linkId} value={props.item.linkId}>
            <Accordion.Control>
              <Text>{props.item.prefix}</Text>
            </Accordion.Control>
            <Accordion.Panel>
              <Text className="whitespace-pre-wrap">{props.item.text}</Text>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      )
    } else {
      return <Text>{props.item.text}</Text>
    }
  }

  const showAddButton =
    item?.repeats &&
    item.type !== QuestionnaireItemType.choice &&
    item.type !== QuestionnaireItemType.openChoice

  if (isHidden(item)) {
    return null
  }

  // Styling reason to avoid duplicate text
  if (item.type === QuestionnaireItemType.boolean) {
    return (
      <QuestionnaireFormItem
        key={item.linkId}
        item={item}
        response={response}
        onChange={(r) => onChange([r])}
        index={0}
      />
    )
  }

  return (
    <FormSection
      key={props.item.linkId}
      htmlFor={props.item.linkId}
      title={props.item.text}
      withAsterisk={props.item.required}
    >
      {[...Array(number)].map((_, index) => (
        <QuestionnaireFormItem
          // oxlint-disable-next-line react/no-array-index-key -- repeats have no identity; "Add Item" only appends.
          key={`${item.linkId}-${index}`}
          item={item}
          response={response}
          onChange={(r) => onChange([r])}
          index={index}
        />
      ))}
      {showAddButton && (
        <Anchor onClick={() => setNumber((n) => n + 1)}>Add Item</Anchor>
      )}
    </FormSection>
  )
}

function getNumberOfRepeats(
  item: QuestionnaireItem,
  response: QuestionnaireResponseItem
): number {
  if (
    item.type === QuestionnaireItemType.choice ||
    item.type === QuestionnaireItemType.openChoice
  ) {
    return 1
  }
  const answers = response.answer
  return answers?.length ? answers.length : 1
}

function isHidden(item: QuestionnaireItem): boolean {
  return !!item.extension?.some(
    (e) =>
      e.url === structureDefinitions.QUESTIONNAIRE_HIDDEN_EXTENSION_URL &&
      e.valueBoolean
  )
}
